import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import creditCardFill from '@iconify/icons-eva/credit-card-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import timeFill from '@iconify/icons-eva/clock-fill';

// constants
import { Status, statusColorMap } from '../constants/ocpi.constant';
import { OCPI_ROLES } from 'src/utils/ocpi.constants';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 800,
    width: '100%'
  }
}));

const DetailItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5)
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  wordBreak: 'break-all'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2)
}));

const TariffIcon = ({ tariffType, size = 40 }) => {
  const getIcon = () => {
    switch (tariffType) {
      case 'REGULAR':
        return creditCardFill;
      case 'FAST':
        return flashFill;
      case 'GREEN':
        return flashFill;
      case 'CHEAP':
        return creditCardFill;
      case 'PREMIUM':
        return creditCardFill;
      case 'OFF_PEAK':
        return timeFill;
      case 'PEAK':
        return flashFill;
      default:
        return creditCardFill;
    }
  };

  return <Icon icon={getIcon()} width={size} height={size} />;
};


const TariffView = ({ open, onClose, tariff, onEdit }) => {
  if (!tariff) return null;
  
  const tariffStatus = tariff.active ? Status.VALID : Status.INVALID;

  const formatPrice = (price, currency) => {
    return `${price} ${currency}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TariffIcon tariffType={tariff.tariffType} />
            <Box>
              <Typography variant="h6" component="h2">
                Tariff Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tariff.tariffId}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon icon={closeFill} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <SectionTitle variant="subtitle1">
              Basic Information
            </SectionTitle>
          </Grid>

          {/* Moved Status and Last Updated into Basic Information */}
          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Status</DetailLabel>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={tariffStatus}
                  color={statusColorMap[tariffStatus]?.color || 'default'}
                  variant="filled"
                  sx={{ 
                    bgcolor: statusColorMap[tariffStatus]?.bgcolor || 'grey.200',
                    color: statusColorMap[tariffStatus]?.text || 'text.secondary',
                  }}
                />
              </Box>
            </DetailItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Last Updated</DetailLabel>
              <DetailValue variant="body1">{formatDate(tariff.lastUpdated)}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Tariff ID</DetailLabel>
              <DetailValue variant="body1">{tariff.id}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Type</DetailLabel>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={tariff.tariffType}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
              </Box>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Country Code</DetailLabel>
              <DetailValue variant="body1">{tariff.countryCode}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Party ID</DetailLabel>
              <DetailValue variant="body1">{tariff.partyId}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Currency</DetailLabel>
              <DetailValue variant="body1">{tariff.currency}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Energy Category</DetailLabel>
              <DetailValue variant="body1">{tariff.energyMix}</DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Minimum Price</DetailLabel>
              <DetailValue variant="body1">
                {tariff.minPrice > 0 ? formatPrice(tariff.minPrice, tariff.currency) : 'No minimum'}
              </DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DetailItem>
              <DetailLabel variant="body2">Maximum Price</DetailLabel>
              <DetailValue variant="body1">
                {tariff.maxPrice ? formatPrice(tariff.maxPrice, tariff.currency) : 'No limit'}
              </DetailValue>
            </DetailItem>
          </Grid>

          <Grid item xs={12}>
            <DetailItem>
              <DetailLabel variant="body2">Description</DetailLabel>
              {tariff?.tariffAltText && tariff.tariffAltText.length > 0 ? (
                <Box>
                  {tariff.tariffAltText.map((altText, index) => (
                    <Box key={index} sx={{ mb: index < tariff.tariffAltText.length - 1 ? 1 : 0 }}>
                      <DetailValue variant="body1">
                        {altText.text}
                        {altText.language && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({altText.language})
                          </Typography>
                        )}
                      </DetailValue>
                    </Box>
                  ))}
                </Box>
              ) : (
                <DetailValue variant="body1">No description</DetailValue>
              )}
            </DetailItem>
          </Grid>

          {/* Pricing Elements */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <SectionTitle variant="subtitle1">
              Pricing Elements
            </SectionTitle>
          </Grid>

          {tariff.elements.map((element, elementIndex) => (
            <Grid item xs={12} key={elementIndex}>
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
                  Element {elementIndex + 1}
                </Typography>
                
                {element.price_components?.map((component, componentIndex) => (
                  <Box key={componentIndex} sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Price Component {componentIndex + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <DetailLabel variant="body2">Type</DetailLabel>
                        <DetailValue variant="body1">{component.type}</DetailValue>
                      </Grid>
                      <Grid item xs={4}>
                        <DetailLabel variant="body2">Price</DetailLabel>
                        <DetailValue variant="body1">
                          {formatPrice(component.price, tariff.currency)}
                        </DetailValue>
                      </Grid>
                      <Grid item xs={4}>
                        <DetailLabel variant="body2">Step Size</DetailLabel>
                        <DetailValue variant="body1">{component.step_size}</DetailValue>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Paper>
            </Grid>
          ))}

          {tariff.elements.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">No pricing elements configured</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {tariff.role !== OCPI_ROLES.CPO && 
        <Button
          onClick={onEdit}
          variant="contained"
          startIcon={<Icon icon="eva:edit-fill" />}
        >
          Edit Tariff
        </Button>}
      </DialogActions>
    </StyledDialog>
  );
};

TariffView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  tariff: PropTypes.shape({
    id: PropTypes.string,
    tariff_id: PropTypes.string,
    type: PropTypes.string,
    tariff_type: PropTypes.string,
    country_code: PropTypes.string,
    party_id: PropTypes.string,
    partner_country_code: PropTypes.string,
    partner_party_id: PropTypes.string,
    currency: PropTypes.string,
    energy_category: PropTypes.string,
    restrictions: PropTypes.string,
    valid: PropTypes.bool,
    last_updated: PropTypes.string,
    updated_at: PropTypes.string,
    description: PropTypes.string,
    min_price: PropTypes.number,
    max_price: PropTypes.number,
    elements: PropTypes.array
  })
};

export default TariffView;
